import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Plus, Edit, Trash2, Mail, User, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../services/api';
import { toast } from 'sonner';

export const SensoryTeamPage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_group_a: false
  });
  const [submitting, setSubmitting] = useState(false);

  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';

  useEffect(() => {
    fetchMembers();
  }, [sensoryType]);

  const fetchMembers = async () => {
    try {
      const response = await api.get('/sensory-team', { params: { sensory_type: sensoryType } });
      setMembers(response.data);
    } catch (error) {
      toast.error('Chyba při načítání členů týmu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email || '',
        is_group_a: member.is_group_a || false
      });
    } else {
      setEditingMember(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        is_group_a: false
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('Vyplňte jméno a příjmení');
      return;
    }

    // Basic email validation (only if email is provided)
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Zadejte platný email');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editingMember) {
        await api.put(`/sensory-team/${editingMember.id}`, formData);
        toast.success('Člen aktualizován');
      } else {
        await api.post('/sensory-team', { ...formData, sensory_type: sensoryType });
        toast.success('Člen přidán');
      }
      setDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Opravdu chcete smazat člena ${member.first_name} ${member.last_name}?`)) {
      return;
    }

    try {
      await api.delete(`/sensory-team/${member.id}`);
      toast.success('Člen smazán');
      fetchMembers();
    } catch (error) {
      toast.error('Chyba při mazání člena');
    }
  };

  return (
    <Layout>
      <div className="p-8" data-testid="sensory-team-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">Senzorický tým - {typeLabel}</h1>
            <p className="text-slate-600">Správa členů senzorického týmu pro {typeLabel}</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Přidat člena
          </Button>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Načítání...</div>
          ) : members.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
                <Users className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Žádní členové</h3>
              <p className="text-slate-500 mb-4">Zatím nemáte žádné členy senzorického týmu</p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Přidat prvního člena
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jméno</TableHead>
                  <TableHead>Příjmení</TableHead>
                  <TableHead>Email</TableHead>
                  {sensoryType === 'krabicky' && (
                    <TableHead className="w-[120px]">Skupina A</TableHead>
                  )}
                  <TableHead className="w-[100px]">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.first_name}</TableCell>
                    <TableCell>{member.last_name}</TableCell>
                    <TableCell>
                      {member.email ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-4 w-4" />
                          {member.email}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    {sensoryType === 'krabicky' && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={member.is_group_a || false}
                            onCheckedChange={async (checked) => {
                              try {
                                await api.put(`/sensory-team/${member.id}`, { is_group_a: checked });
                                toast.success(checked ? 'Přidán do skupiny A' : 'Odebrán ze skupiny A');
                                fetchMembers();
                              } catch (error) {
                                toast.error('Chyba při změně skupiny');
                              }
                            }}
                          />
                          {member.is_group_a && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              <FlaskConical className="h-3 w-3 mr-1" />
                              A
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(member)}
                        >
                          <Edit className="h-4 w-4 text-slate-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(member)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Stats */}
        {members.length > 0 && (
          <div className="mt-4 text-sm text-slate-500">
            Celkem {members.length} {members.length === 1 ? 'člen' : members.length < 5 ? 'členové' : 'členů'}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingMember ? 'Upravit člena' : 'Nový člen týmu'}
              </DialogTitle>
              <DialogDescription>
                {editingMember 
                  ? 'Upravte údaje člena senzorického týmu'
                  : 'Přidejte nového člena senzorického týmu'
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Jméno *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="např. Jan"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="last_name">Příjmení *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="např. Novák"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="např. jan.novak@firma.cz (nepovinné)"
                />
              </div>

              {sensoryType === 'krabicky' && (
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <Label htmlFor="is_group_a" className="text-purple-800 font-medium">Skupina A (rozšířené hodnocení)</Label>
                    <p className="text-sm text-purple-600 mt-1">Člen bude hodnotit dodatečná pole pro skupinu A</p>
                  </div>
                  <Switch
                    id="is_group_a"
                    checked={formData.is_group_a}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_group_a: checked })}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-gradient-to-r from-teal-500 to-emerald-500"
              >
                {submitting ? 'Ukládám...' : (editingMember ? 'Uložit změny' : 'Přidat člena')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SensoryTeamPage;
